// ═══════════════════════════════════════════════════════════════════════
//  SITE CONFIG — make this portfolio yours!
//
//  This is the ONLY file you need to edit. Everything on the site
//  (your name, bio, projects, links, and the posts feed) is driven
//  from here.
//
//  >> MOST IMPORTANT: replace `ownerNpub` below with YOUR npub.
//     Your public Nostr posts will automatically stream into the
//     "What I've been up to" feed. (You can find your npub in any
//     Nostr client — it starts with "npub1…".)
// ═══════════════════════════════════════════════════════════════════════

export const siteConfig = {
  /** Your Nostr public key. The feed shows notes & reposts by this key.
   *  (The placeholder below is an active Bitcoiner so you can see the
   *  feed working — swap in your own npub!) */
  ownerNpub: 'npub1qny3tkh0acurzla8x3zy4nhrjz5zd8l9sy9jys09umwng00manysew95gx',

  /** Your display name, shown across the site. */
  name: 'Sats Sister',

  /** Short handle used in the terminal card & footer. */
  handle: 'satssister',

  /** Set this to true to pull your name, avatar, and banner straight
   *  from your Nostr profile (kind 0) instead of using the values here. */
  useNostrProfile: false,

  /** One-liner shown under the big headline. */
  tagline:
    'Open-source developer shipping freedom tech on Bitcoin & Nostr. ' +
    'Privacy enjoyer, node runner, serial side-project starter.',

  /** Your lightning address for zaps (shown in the footer). If your Nostr
   *  profile has one set, that takes priority. */
  lightningAddress: 'satssister@walletofsatoshi.com',

  /** The big typewriter-style line in the hero. */
  heroLine1: 'I build open-source tools on',
  heroHighlightA: 'Bitcoin',
  heroHighlightB: 'Nostr',

  /** About section — a few short paragraphs about you. */
  bio: [
    "I'm a self-taught developer who fell down the Bitcoin rabbit hole and never climbed out. These days I spend my time building open-source tools that help people hold their own keys, run their own infrastructure, and communicate without permission.",
    'Most of my work lives at the intersection of Bitcoin and Nostr: signing devices, relay tooling, zap-powered apps, and little utilities that make sovereign computing feel a little more human.',
    "When I'm not pushing commits, I'm probably testing a new NIP, breaking my node on purpose, or orange-pilling my group chat.",
  ],

  /** Things you care about (rendered as a checklist in About). */
  values: [
    'Free & open-source software, always',
    'Self-custody and privacy by default',
    'Credible neutrality, no gatekeepers',
    'Ship small, ship often, ship in public',
  ],

  /** Skills rendered as chips in About. */
  skills: [
    'TypeScript',
    'Rust',
    'React',
    'Bitcoin Core',
    'Lightning / LDK',
    'Nostr NIPs',
    'PSBT & Multisig',
    'NIP-46 Signers',
    'Cryptography',
    'Nix',
  ],

  /** Lines for the fake terminal in About. Keep them short & fun. */
  terminal: [
    { cmd: 'whoami', out: 'sats-sister — she/her · bitcoiner · builder' },
    { cmd: 'cat mission.txt', out: 'fix the money, fix the world. but actually ship.' },
    { cmd: 'git log --oneline -3', out: 'a1b2c3d zap ui polish\nf4e5d6a add nip-46 bunker flow\n007beef fix: node wont sync (it was dns)' },
    { cmd: 'sudo systemctl status vibes', out: '● active (orange-pilled) since block 710,000' },
  ],

  /** Your projects. Replace these with your real repos! Set `featured: true`
   *  on one to make it span two columns. Links are optional. */
  projects: [
    {
      name: 'relay-keeper',
      description:
        'A tiny Nostr relay health monitor. Pings your relay list, tracks uptime & latency, and posts a daily status note so your followers know which relays are alive.',
      tags: ['TypeScript', 'Nostr', 'NIP-66'],
      repo: 'https://github.com/satssister/relay-keeper',
      featured: true,
      status: 'active',
    },
    {
      name: 'psbt-studio',
      description:
        'A browser-based PSBT builder and decoder for multisig workflows. Paste, inspect, sign, export — no server, no custody, all client-side.',
      tags: ['Bitcoin', 'PSBT', 'React'],
      repo: 'https://github.com/satssister/psbt-studio',
      status: 'active',
    },
    {
      name: 'zapboard',
      description:
        'Leaderboard that aggregates NIP-57 zaps for open-source contributors, so the people maintaining your favorite tools actually get paid.',
      tags: ['Nostr', 'Lightning', 'NIP-57'],
      repo: 'https://github.com/satssister/zapboard',
      status: 'maintained',
    },
    {
      name: 'nsec-bunker-ui',
      description:
        'A friendly dashboard for managing NIP-46 remote signers: connect apps, review permissions, revoke sessions — without touching a config file.',
      tags: ['Nostr', 'NIP-46', 'Security'],
      repo: 'https://github.com/satssister/nsec-bunker-ui',
      status: 'experiment',
    },
    {
      name: 'fee-oracle-bot',
      description:
        'A little Nostr bot that posts mempool fee estimates and freshly-mined block stats every hour. Follow it to time your transactions.',
      tags: ['Bitcoin', 'Mempool', 'Bot'],
      repo: 'https://github.com/satssister/fee-oracle-bot',
      status: 'maintained',
    },
    {
      name: 'sats4devs',
      description:
        'An embeddable tip-jar widget that lets any static site receive Lightning donations via LNURL. One script tag, zero accounts.',
      tags: ['Lightning', 'LNURL', 'Widget'],
      repo: 'https://github.com/satssister/sats4devs',
      status: 'experiment',
    },
  ],

  /** Where people can find you. Leave a field empty to hide it. */
  links: {
    github: 'https://github.com/satssister',
    website: '',
  },
} as const;

export type SiteConfig = typeof siteConfig;
